import React from "react";
import { Droplets, Sun, Wind, Shirt, Sparkles, AlertTriangle } from "lucide-react";

export default function CareGuide() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="care-guide-page">
      <div className="text-center mb-12">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-3">Saree Care</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">Care Guide</h1>
        <div className="gold-divider mt-6"><span className="text-lg">✦</span></div>
        <p className="text-[#7c7273] mt-6 font-italic-serif text-lg">A well-loved saree lasts a lifetime.</p>
      </div>

      {/* Cotton */}
      <section className="mb-10" data-testid="care-cotton">
        <h2 className="font-serif text-2xl text-[#2b1b1c] flex items-center gap-3">
          <span className="w-1.5 h-6 bg-[#c9a34a]" />Cotton Sarees
        </h2>
        <p className="text-[#2b1b1c] mt-3 leading-relaxed">
          Handloom cottons are breathable and get softer with every wear. A little gentle care keeps their crispness and colour for years.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Tip icon={<Droplets size={18} className="text-[#4a7a59]" />} title="First wash">
            Dry-clean the first time to lock in colour. After that, hand-wash separately in cold water with a mild, colour-safe detergent.
          </Tip>
          <Tip icon={<Wind size={18} className="text-[#4a7a59]" />} title="Gentle handling">
            Do not scrub, wring, or twist. Squeeze water out gently by pressing between palms — never bunch or twist the fabric.
          </Tip>
          <Tip icon={<Sun size={18} className="text-[#4a7a59]" />} title="Drying">
            Dry in the shade, hung inside out — direct sunlight fades cotton. Let it drip-dry; avoid tumble drying.
          </Tip>
          <Tip icon={<Shirt size={18} className="text-[#4a7a59]" />} title="Ironing">
            Iron on medium heat while slightly damp. Iron the reverse side for prints or block-printed cottons to protect motifs.
          </Tip>
          <Tip icon={<Sparkles size={18} className="text-[#4a7a59]" />} title="Storage">
            Store folded in a cotton or muslin bag. Refold every 2–3 months to prevent permanent creases along the folds.
          </Tip>
          <Tip icon={<AlertTriangle size={18} className="text-[#b58600]" />} title="Avoid">
            No bleach, no harsh detergents, no perfume/deodorant directly on the fabric. Blot stains — never rub.
          </Tip>
        </div>

        <div className="bg-[#f5f3ef] border-l-2 border-[#c9a34a] px-5 py-4 mt-6 text-sm text-[#2b1b1c]">
          <strong className="font-serif">Pro tip:</strong> Add a spoon of rock salt to the first wash — it helps set natural dyes and keeps colours vivid.
        </div>
      </section>

      {/* Silk (Banarasi, Kanjivaram, Silk blends) */}
      <section className="mb-10" data-testid="care-silk">
        <h2 className="font-serif text-2xl text-[#2b1b1c] flex items-center gap-3">
          <span className="w-1.5 h-6 bg-[#c9a34a]" />Silk Sarees
        </h2>
        <p className="text-[#2b1b1c] mt-3 leading-relaxed">
          Pure silks &mdash; Banarasi, Kanjivaram, Paithani, Tussar and their kin &mdash; are heirlooms. Treat them the way you would a piece of jewellery: careful, spare use, and always professionally cleaned.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <Tip icon={<Droplets size={18} className="text-[#7a1e2b]" />} title="Cleaning">
            <strong>Dry-clean only.</strong> Never wash pure silk at home &mdash; water dulls the sheen, warps the weave, and can bleed dyes. Use a reputable cleaner who understands sarees.
          </Tip>
          <Tip icon={<Sun size={18} className="text-[#7a1e2b]" />} title="Sun &amp; light">
            Keep silks out of direct sunlight &mdash; even a few hours can fade rich reds, indigos and greens. Store away from windows.
          </Tip>
          <Tip icon={<Wind size={18} className="text-[#7a1e2b]" />} title="Air after wearing">
            Hang your silk in a well-ventilated (shaded) spot for 3&ndash;4 hours after each wear so any body moisture and perfume can dissipate before it's folded back.
          </Tip>
          <Tip icon={<Shirt size={18} className="text-[#7a1e2b]" />} title="Ironing">
            Iron only on the reverse, on the <strong>low silk setting</strong>, with a thin cotton cloth in between. Never spray water directly &mdash; it stains. Avoid steaming zari.
          </Tip>
          <Tip icon={<Sparkles size={18} className="text-[#c9a34a]" />} title="Storage — refold monthly">
            Fold along different creases every 4&ndash;6 weeks to stop zari and gota work from cracking. Wrap in a soft muslin or cotton cloth &mdash; never in plastic (silk needs to breathe).
          </Tip>
          <Tip icon={<AlertTriangle size={18} className="text-[#b58600]" />} title="Avoid">
            No perfume, deodorant, hair spray or lotion on the fabric. No safety pins into zari borders. No hangers &mdash; the weight will distort the drape.
          </Tip>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="bg-[#f5f3ef] border-l-2 border-[#7a1e2b] px-5 py-4 text-sm text-[#2b1b1c]">
            <div className="font-serif text-lg mb-1">Banarasi &amp; Kanjivaram (heavy zari)</div>
            <ul className="mt-2 space-y-1.5">
              <li>&bull; Wrap the zari border inwards when folding to protect the gold threads.</li>
              <li>&bull; Slip a strip of muslin between folds so metallic threads don&rsquo;t rub against silk.</li>
              <li>&bull; Air after every 2&ndash;3 months of storage &mdash; even unworn silks need to breathe.</li>
              <li>&bull; If a zari thread lifts, do <em>not</em> pull it &mdash; snip level with the surface using tiny scissors.</li>
            </ul>
          </div>
          <div className="bg-[#f5f3ef] border-l-2 border-[#c9a34a] px-5 py-4 text-sm text-[#2b1b1c]">
            <div className="font-serif text-lg mb-1">Lighter silks (Tussar, Chanderi, Maheshwari)</div>
            <ul className="mt-2 space-y-1.5">
              <li>&bull; Still dry-clean only &mdash; but they tolerate a gentler press and softer folds.</li>
              <li>&bull; Store on a lower shelf with a cotton lining &mdash; humidity is their enemy.</li>
              <li>&bull; A muslin sachet of dry cloves in the fold discourages silverfish naturally.</li>
              <li>&bull; Refresh with a cool iron on reverse before wearing to lift the drape.</li>
            </ul>
          </div>
        </div>

        <div className="bg-white border border-[#e8e4db] px-5 py-4 mt-6 text-sm text-[#2b1b1c]">
          <strong className="font-serif text-[#7a1e2b]">First outing tip:</strong> Before you drape a new silk, iron it on the reverse and hang it for 30 minutes &mdash; the drape falls softer, and the pallu sits right.
        </div>
      </section>

      {/* General care */}
      <section className="mb-10" data-testid="care-general">
        <h2 className="font-serif text-2xl text-[#2b1b1c] flex items-center gap-3">
          <span className="w-1.5 h-6 bg-[#c9a34a]" />General Saree Care
        </h2>
        <ul className="mt-4 space-y-3 text-[#2b1b1c] leading-relaxed">
          <li className="flex gap-3"><span className="text-[#c9a34a] mt-1">✦</span><span>Air your saree after every wear — hang it for a few hours before folding.</span></li>
          <li className="flex gap-3"><span className="text-[#c9a34a] mt-1">✦</span><span>Refold along different creases every couple of months to prevent zari or embellishments from cracking.</span></li>
          <li className="flex gap-3"><span className="text-[#c9a34a] mt-1">✦</span><span>Store away from moisture and mothballs — a small muslin sachet of dry cloves works beautifully.</span></li>
          <li className="flex gap-3"><span className="text-[#c9a34a] mt-1">✦</span><span>For silks, Banarasi, Kanjivaram and other zari weaves — always dry-clean only.</span></li>
        </ul>
      </section>

      <p className="text-center text-xs text-[#7c7273] tracking-wider uppercase mt-14">
        Care instructions for specialty weaves like Ajrakh, Bandhani &amp; Kalamkari — coming soon.
      </p>
    </div>
  );
}

function Tip({ icon, title, children }) {
  return (
    <div className="bg-white border border-[#e8e4db] p-4">
      <div className="flex items-center gap-2">
        {icon}
        <div className="font-serif text-[#2b1b1c]">{title}</div>
      </div>
      <p className="text-sm text-[#7c7273] mt-2 leading-relaxed">{children}</p>
    </div>
  );
}
